import { Link } from 'react-router-dom';
import InkPhDLogo, { InkPhDMark } from '@/components/InkPhDLogo';
import SeoHead from '@/components/SeoHead';
import AppFooter from '@/components/AppFooter';

const ASSETS = [
  ['/logo.png', 'Wordmark — light surfaces', 'Header, documents, invoices, letterheads.'],
  ['/logo-dark.png', 'Wordmark — dark surfaces', 'Dark UI, the social card, presentation slides.'],
  ['/favicon.png', 'Mark — bare', 'Browser tab, favicons, tight inline spots.'],
  ['/icon-192.png', 'Mark — navy plate', 'App icon, maskable PWA icon, social avatar.'],
  ['/og-image.png', 'Social card 1200×630', 'Open Graph and Twitter previews on every public page.'],
];

export default function BrandPage() {
  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <SeoHead title="Brand assets" path="/brand" noindex
        description="The official InkPhD logo, mark, app icons and social card." />
      <header className="border-b border-border">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/"><InkPhDLogo size="md" /></Link>
          <span className="text-xs text-muted-foreground">Brand assets</span>
        </div>
      </header>

      <main className="flex-1 max-w-5xl mx-auto w-full px-4 py-12 space-y-12" data-testid="brand-page">
        <div>
          <h1 className="text-4xl font-serif font-bold tracking-tight mb-2"
            style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
            Brand assets
          </h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            The official artwork is now wired through the whole product: header, favicon, app icons
            and the social preview card. The dark version is generated from the same file, so the
            two can never drift apart.
          </p>
        </div>

        <div className="rounded-xl border border-border overflow-hidden" data-testid="brand-preview">
          <div className="p-10 bg-white flex flex-col items-center gap-6">
            <InkPhDLogo size="xl" />
            <div className="flex items-center gap-6">
              <InkPhDLogo size="md" />
              <InkPhDLogo size="sm" />
              <InkPhDMark height={26} />
              <InkPhDMark height={26} plated />
            </div>
          </div>
          <div className="p-10 bg-[#0b1a35] flex flex-col items-center gap-6">
            <InkPhDLogo size="xl" variant="dark" />
            <div className="flex items-center gap-6">
              <InkPhDLogo size="md" variant="dark" />
              <InkPhDMark height={26} plated />
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {ASSETS.map(([src, title, use]) => (
            <a key={src} href={src} download
              className="rounded-xl border border-border p-4 hover:border-[#1d4ed8]/50 transition-colors"
              data-testid={`brand-asset-${src.replace(/[^a-z0-9]/gi, '-')}`}>
              <div className={`h-20 flex items-center justify-center rounded mb-3 ${
                src.includes('dark') || src.includes('og-') ? 'bg-[#0b1a35]' : 'bg-white'}`}>
                <img src={src} alt={title} className="max-h-16 max-w-[80%] object-contain" />
              </div>
              <div className="text-[13px] font-medium">{title}</div>
              <p className="text-[12px] text-muted-foreground">{use}</p>
              <span className="text-[11px] text-[#1d4ed8]">download {src}</span>
            </a>
          ))}
        </div>

        <div className="rounded-xl border border-border p-6 space-y-2">
          <h2 className="font-serif text-lg">Usage</h2>
          <ul className="text-[13px] text-muted-foreground space-y-1 list-disc list-inside">
            <li>Keep clear space of at least the height of the bracket on every side.</li>
            <li>Never re-colour the wordmark by hand — use <code className="text-[11px]">logo-dark.png</code> on dark surfaces.</li>
            <li>Ink <code className="text-[11px]">#0b0b0b</code> · accent blue <code className="text-[11px]">#0034fb</code> ·
              dark-surface ink <code className="text-[11px]">#f8fafc</code> · dark-surface accent <code className="text-[11px]">#7aa9ff</code>.</li>
            <li>Regenerate every derivative with <code className="text-[11px]">python3 /app/scripts/brand_assets.py</code>.</li>
          </ul>
        </div>
      </main>
      <AppFooter />
    </div>
  );
}
